import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faPlus,
  faPen,
  faImage,
  faBan,
  faSearch,
  faHandsHelping,
  faCircleExclamation,
  faCircleCheck,
  faXmark,
  faLink,
  faCopy,
  faVolumeHigh,
  faVolumeXmark,
} from "@fortawesome/free-solid-svg-icons";

import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import AILightComponent from "../../components/AILightComponent";

import { useHerKart } from "./hooks/useHerKart";
import WebpImgUploader from "../purpledoor/modules/components/WebpImgUploader";

const emptyForm = {
  name: "",
  category: "",
  description: "",
  price: "",
  duration: "",
  entrepreneur: "",
  store: "",
  locality: "",
  city: "",
  storeCode: "",
  image: "",
  tts: "",
  isActive: true,
};

const HerKartManageServices = () => {
  const authState = useSelector((state) => state.authReducer);
  const {
    fetchMyServices,
    createService,
    updateService,
    deactivateService,
    fetchMyStores,
  } = useHerKart();

  const [services, setServices] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [uploaderForId, setUploaderForId] = useState(null);
  const [confirmDeactivateId, setConfirmDeactivateId] = useState(null);

  const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
  const toggleGenAIHelp = () => setEnableGenAIHelp((prev) => !prev);

  const [isSpeakingTTS, setIsSpeakingTTS] = useState(false);
  const handleToggleSpeakTTS = () => {
    if (!("speechSynthesis" in window)) return;
    if (isSpeakingTTS) {
      window.speechSynthesis.cancel();
      setIsSpeakingTTS(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(form.tts);
    utterance.onend = () => setIsSpeakingTTS(false);
    utterance.onerror = () => setIsSpeakingTTS(false);
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setIsSpeakingTTS(true);
  };

  const [linkForId, setLinkForId] = useState(null);
  const buildServiceLink = (service) =>
    `https://www.think5whys.com/herkart/s/${service.serviceCode || service._id}`;

  const handleCopyLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      setMessage({ type: "success", text: "Link copied to clipboard." });
    } catch (e) {
      setMessage({ type: "error", text: "Could not copy link." });
    }
  };

  const handleShareOnWhatsApp = (service) => {
    const link = buildServiceLink(service);
    const text = encodeURIComponent(`Check out ${service.name} on HerKart: ${link}`);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const load = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [servicesRes, storesRes] = await Promise.all([
        fetchMyServices(),
        fetchMyStores(),
      ]);
      if (servicesRes?.success) setServices(servicesRes.services || []);
      if (storesRes?.success) setStores(storesRes.stores || []);
    } catch (e) {
      console.error("Services fetch error:", e);
      setMessage({ type: "error", text: "Could not load your services." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredServices = useMemo(() => {
    let list = [...services];

    if (statusFilter !== "All") {
      list = list.filter((s) =>
        statusFilter === "Active" ? s.isActive : !s.isActive,
      );
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((s) =>
        [s.name, s.category].join(" ").toLowerCase().includes(keyword),
      );
    }

    return list;
  }, [services, search, statusFilter]);

  const openAddForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
    setMessage(null);
  };

  const openEditForm = (service) => {
    setEditingId(service._id);
    setForm({
      name: service.name || "",
      category: service.category || "",
      description: service.description || "",
      price: service.price ?? "",
      duration: service.duration || "",
      entrepreneur: service.entrepreneur || "",
      store: service.store || "",
      locality: service.locality || "",
      city: service.city || "",
      storeCode: service.storeCode || "",
      image: service.image || "",
      tts: service.tts || "",
      isActive: !!service.isActive,
    });
    setShowForm(true);
    setMessage(null);
  };

  const closeForm = () => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    setIsSpeakingTTS(false);
    setEnableGenAIHelp(false);
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "storeCode") {
      const selectedStore = stores.find((s) => s.storeCode === value);
      setForm((prev) => ({
        ...prev,
        storeCode: value,
        store: selectedStore?.storeName || prev.store,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!form.name.trim() || !form.price) {
      setMessage({
        type: "error",
        text: "Service Name and Price are required.",
      });
      return;
    }

    const payload = { ...form, price: Number(form.price) };

    setSaving(true);
    try {
      if (editingId) {
        const response = await updateService(editingId, payload);
        if (response?.success) {
          setServices((prev) =>
            prev.map((s) => (s._id === editingId ? response.service : s)),
          );
          setMessage({
            type: "success",
            text: "Service updated successfully.",
          });
          closeForm();
        } else {
          setMessage({ type: "error", text: "Failed to update service." });
        }
      } else {
        const response = await createService(payload);
        if (response?.success) {
          setServices((prev) => [response.service, ...prev]);
          setMessage({
            type: "success",
            text: "Service created successfully.",
          });
          closeForm();
        } else {
          setMessage({ type: "error", text: "Failed to create service." });
        }
      }
    } catch (e) {
      console.error("Service save error:", e);
      setMessage({ type: "error", text: "Failed to save service." });
    } finally {
      setSaving(false);
    }
  };

  const confirmDeactivate = async (id) => {
    try {
      const response = await deactivateService(id);
      if (response?.success) {
        setServices((prev) =>
          prev.map((s) => (s._id === id ? response.service : s)),
        );
        setMessage({ type: "success", text: "Service deactivated." });
      } else {
        setMessage({ type: "error", text: "Failed to deactivate service." });
      }
    } catch (e) {
      console.error("Deactivate error:", e);
      setMessage({ type: "error", text: "Failed to deactivate service." });
    } finally {
      setConfirmDeactivateId(null);
    }
  };

  const handleReactivate = async (id) => {
    try {
      const response = await updateService(id, { isActive: true });
      if (response?.success) {
        setServices((prev) =>
          prev.map((s) => (s._id === id ? response.service : s)),
        );
        setMessage({ type: "success", text: "Service reactivated." });
      } else {
        setMessage({ type: "error", text: "Failed to reactivate service." });
      }
    } catch (e) {
      console.error("Reactivate error:", e);
      setMessage({ type: "error", text: "Failed to reactivate service." });
    }
  };

  const handleImageChange = async (serviceId, filename, url) => {
    if (serviceId === "new") {
      setForm((prev) => ({ ...prev, image: url }));
      setUploaderForId(null);
      return;
    }

    try {
      const response = await updateService(serviceId, { image: url });
      if (response?.success) {
        setServices((prev) =>
          prev.map((s) => (s._id === serviceId ? response.service : s)),
        );
        setMessage({ type: "success", text: "Service image updated." });
      }
    } catch (e) {
      console.error("Image update error:", e);
      setMessage({ type: "error", text: "Failed to update service image." });
    } finally {
      setUploaderForId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/herkart/dashboard"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-pink-300">My Services</h1>
              <p className="text-xs text-slate-200">
                Manage the services you offer
              </p>
            </div>
          </div>

          <button
            onClick={openAddForm}
            title="Add New Service"
            className="flex h-10 items-center gap-2 rounded-xl bg-pink-600 w-fit px-2 py-1 hover:bg-pink-500"
          >
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        {message && (
          <div
            className={`mb-4 flex items-center gap-3 rounded-2xl border p-4 text-sm ${
              message.type === "success"
                ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-200"
                : "border-red-500/20 bg-red-500/10 text-red-200"
            }`}
          >
            <FontAwesomeIcon
              icon={
                message.type === "success" ? faCircleCheck : faCircleExclamation
              }
            />
            {message.text}
          </div>
        )}

        {stores.length === 0 && !loading && (
          <div className="mb-4 flex flex-col lg:flex-row items-center gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 text-sm text-amber-200">
            <FontAwesomeIcon icon={faCircleExclamation} />
            You don't have any private stores yet.{" "}
            <Link to="/herkart/manage-stores" className="underline">
              Create one first
            </Link>{" "}
            so you can link your products to it and make your exclusive private
            online store with your own products..
          </div>
        )}

        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or category..."
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 outline-none focus:border-pink-500"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
            >
              <option>All</option>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>

        {showForm && (
          <div className="mb-6 rounded-3xl border border-pink-500/30 bg-white/5 p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-lg font-bold text-pink-300">
                {editingId ? "Edit Service" : "Add New Service"}
              </h2>
              <button
                onClick={closeForm}
                className="w-fit px-1 py-1 text-slate-300 hover:text-white"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={form.category}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm text-gray-200">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleFormChange}
                    style={{ height: "100px" }}
                    maxLength={250}
                    className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Price *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={form.price}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Duration
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={form.duration}
                    onChange={handleFormChange}
                    placeholder="e.g. 45 mins, 1 session"
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Entrepreneur
                  </label>
                  <input
                    type="text"
                    name="entrepreneur"
                    value={form.entrepreneur}
                    onChange={handleFormChange}
                    placeholder="Provider / entrepreneur name"
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Locality
                  </label>
                  <input
                    type="text"
                    name="locality"
                    value={form.locality}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm text-gray-200">
                    Store
                  </label>
                  <select
                    name="storeCode"
                    value={form.storeCode}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
                  >
                    <option value="">— Select Store —</option>
                    {stores.map((s) => (
                      <option key={s._id} value={s.storeCode}>
                        {s.storeName} ({s.storeCode})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={form.isActive}
                      onChange={handleFormChange}
                    />
                    Active
                  </label>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm text-gray-200">
                  Service Image
                </label>

                {editingId ? (
                  <p className="text-xs text-gray-400">
                    Use the image icon on the service list to change this service's image.
                  </p>
                ) : (
                  <p className="text-xs text-gray-400">
                    Save this service first — you'll be able to add an image
                    right after.
                  </p>
                )}
              </div>

              <div>
                <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-pink-300">
                  Voice Script (Text-to-Speech)
                </h3>
                <textarea
                  name="tts"
                  value={form.tts}
                  onChange={handleFormChange}
                  maxLength={500}
                  style={{ height: "100px" }}
                  placeholder="A short spoken script that will be read aloud to customers browsing this service..."
                  className="w-full resize-none rounded-xl border border-white/10 bg-[#0b1120] px-4 py-3 outline-none focus:border-pink-500"
                />
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-xs text-slate-400">{form.tts.length}/500 characters</p>
                  {form.tts.trim() && (
                    <button
                      type="button"
                      onClick={handleToggleSpeakTTS}
                      title={isSpeakingTTS ? "Stop" : "Play TTS Script"}
                      className={`flex h-8 w-8 items-center justify-center rounded-full ${
                        isSpeakingTTS ? "bg-pink-500 text-white" : "bg-white/10 text-pink-300 hover:bg-white/20"
                      }`}
                    >
                      <FontAwesomeIcon icon={isSpeakingTTS ? faVolumeXmark : faVolumeHigh} />
                    </button>
                  )}
                </div>

                <div className="mt-3 flex flex-col w-full gap-2">
                  <button
                    type="button"
                    onClick={toggleGenAIHelp}
                    className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold ${
                      enableGenAIHelp ? "bg-purple-500 text-white" : "bg-[#111827] text-purple-300 border border-purple-500/30"
                    }`}
                  >
                    {enableGenAIHelp ? "🙈 Hide GenAI" : "🤖 Ask GenAI"}
                  </button>

                  {enableGenAIHelp && (
                    <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
                      <AILightComponent
                        mode="herkart_service_tts_script"
                        param1={"🗣️ AI Voice Script Generator"}
                        param2={`
You are an expert Voice Commerce Copywriter creating a Text-to-Speech (TTS) script for a browser voice assistant that will read this service aloud to a customer on a platform called HerKart.

Service Details:
Name: ${form.name || "N/A"}
Category: ${form.category || "N/A"}
Description: ${form.description || "N/A"}
Entrepreneur/Provider: ${form.entrepreneur || "N/A"}
Price: ${form.price || "N/A"} rupees
Duration: ${form.duration || "N/A"}
Locality/City: ${form.locality || ""} ${form.city || ""}

STRICT RULES:
1. Output ONLY the final spoken script text. No headings, labels, emojis, markdown, quotation marks, bullet points.
2. Maximum 500 characters INCLUDING spaces and punctuation.
3. Short, simple, natural spoken sentences; spell out symbols like rupees instead of ₹.
4. Mention the service name, what it offers, who provides it, and the price, in a friendly tone.
5. Do NOT invent facts not present above.
6. End with a short call to action like "Book yours today".
Return only the final script text.
`}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-pink-600 w-fit px-2 py-1 font-semibold hover:bg-pink-500 disabled:opacity-60"
                >
                  {saving
                    ? "Saving..."
                    : editingId
                      ? "Update Service"
                      : "Create Service"}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="rounded-xl border border-white/10 w-fit px-2 py-1 hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-gray-200">
            Loading your services...
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
            <FontAwesomeIcon
              icon={faHandsHelping}
              className="mx-auto mb-4 text-4xl text-slate-300"
            />
            <h3 className="text-xl font-semibold text-yellow-100">
              No services yet
            </h3>
            <p className="mt-2 text-sm text-gray-200">
              {services.length === 0
                ? "Add your first service to get started."
                : "No services match your search or filter."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredServices .filter((service) => service._id !== editingId) .map((service) => (
              <div
                key={service._id}
                className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 md:flex-row md:items-center"
              >
                <img
                  src={
                    service.image ||
                    "https://placehold.co/100x100/18181b/f9a8d4?text=Service"
                  }
                  alt={service.name}
                  className="h-16 w-16 rounded-xl object-cover"
                />

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold text-gray-100">
                      {service.name}
                    </h3>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        service.isActive
                          ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-red-500/20 text-red-300"
                      }`}
                    >
                      {service.isActive ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <p className="text-sm text-gray-200">
                    {service.category}{" "}
                    {service.storeCode ? `• Store: ${service.storeCode}` : ""}
                  </p>
                  <p className="text-sm text-gray-200">
                    ₹{service.price}{" "}
                    {service.duration ? `· ${service.duration}` : ""}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditForm(service)}
                    title="Edit"
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                  >
                    <FontAwesomeIcon icon={faPen} />
                  </button>

                  <button
                    onClick={() =>
                      setUploaderForId(
                        uploaderForId === service._id ? null : service._id,
                      )
                    }
                    title="Change Image"
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                  >
                    <FontAwesomeIcon icon={faImage} />
                  </button>

                  <button
                    onClick={() => setLinkForId(linkForId === service._id ? null : service._id)}
                    title="Create Service Link"
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                  >
                    <FontAwesomeIcon icon={faLink} />
                  </button>

                  {service.isActive ? (
                    <button
                      onClick={() => setConfirmDeactivateId(service._id)}
                      title="Deactivate"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-600/20 text-red-300 hover:bg-red-600/30"
                    >
                      <FontAwesomeIcon icon={faBan} />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleReactivate(service._id)}
                      title="Reactivate"
                      className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30"
                    >
                      <FontAwesomeIcon icon={faCircleCheck} />
                    </button>
                  )}
                </div>

                {uploaderForId === service._id && (
                  <div className="w-full md:w-auto">
                    <WebpImgUploader
                      label="Add/Edit Service Image"
                      value={service.image}
                      token={authState?.token}
                      uploadUrl="/upload-herkartserviceimage"
                      extraHeaders={{ "x-service-id": service._id }}
                      onChange={(filename, url) =>
                        handleImageChange(service._id, filename, url)
                      }
                      onClose={() => setUploaderForId(null)}
                    />
                  </div>
                )}

                {linkForId === service._id && (
                  <div className="w-full mt-2 rounded-xl border border-white/10 bg-[#0b1120] p-3 flex flex-col md:flex-row md:items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={buildServiceLink(service)}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 rounded-lg border border-white/10 bg-transparent px-3 py-2 text-sm text-slate-200 outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCopyLink(buildServiceLink(service))}
                        title="Copy Link"
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      <button
                        onClick={() => handleShareOnWhatsApp(service)}
                        title="Share on WhatsApp"
                        className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/30"
                      >
                        <FontAwesomeIcon icon={faWhatsapp} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {confirmDeactivateId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0b1120] p-6">
            <h3 className="text-lg font-bold text-orange-200">
              Deactivate this service?
            </h3>
            <p className="mt-2 text-sm text-gray-200">
              This service will no longer be visible to customers. You can't
              undo this from here.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => confirmDeactivate(confirmDeactivateId)}
                className="flex-1 rounded-xl bg-red-600 w-fit px-2 py-1 font-semibold hover:bg-red-500"
              >
                Yes, Deactivate
              </button>
              <button
                onClick={() => setConfirmDeactivateId(null)}
                className="flex-1 rounded-xl border border-white/10 w-fit px-2 py-1 hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerKartManageServices;
